import { debounce } from '../../utils/debounce'
import BaseComponent from '../base-component/base-component'

class ScrollingTextLine extends BaseComponent {
  initialScreenWidth: number
  announcementSelector: string
  animationClass: string
  fontMixedAttribute: string
  fontOutlineClass: string
  isWithFontMixed: boolean
  screenSizeMultiply: number
  resizeObserver: ResizeObserver

  constructor() {
    super()

    this.initialScreenWidth = 0
    this.screenSizeMultiply = 2

    this.announcementSelector = '[data-scrolling-text-line]'
    this.animationClass = 'scrolling-text--animated'
    this.fontMixedAttribute = 'with-font-mixed'
    this.fontOutlineClass = 'scrolling-text__text--font-outline'

    this.isWithFontMixed = this.hasAttribute(this.fontMixedAttribute)

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(document.body)
  }

  disconnectedCallback(): void {
    this.resizeObserver.disconnect()
  }

  getDimentions = (): { screenWidth: number; announcementWidth: number } => {
    const announcement = this.querySelector(this.announcementSelector)

    if (!announcement) {
      return
    }

    const screenWidth = document.body.clientWidth
    const announcementWidth = announcement.clientWidth

    this.initialScreenWidth = screenWidth

    return { screenWidth, announcementWidth }
  }

  calcQuantityToRender = (
    screenWidth: number,
    announcementWidth: number,
  ): number => {
    const blocksNumberToRender = Math.round(
      (screenWidth * this.screenSizeMultiply) / announcementWidth,
    )

    return blocksNumberToRender % 2 === 0
      ? blocksNumberToRender
      : blocksNumberToRender + 1
  }

  renderNodes = (blocksNumberToRender: number): void => {
    const announcement = this.querySelector(this.announcementSelector)

    if (!announcement) {
      return
    }

    const nodes = this.isWithFontMixed
      ? this.getFragmentWithMixedFont(blocksNumberToRender, announcement)
      : this.getFragment(blocksNumberToRender, announcement)

    announcement.classList.add(this.animationClass)

    this.replaceChildren(nodes)
  }

  getFragment = (blocksNumberToRender, announcement): DocumentFragment => {
    const fragment = document.createDocumentFragment()

    for (let i = 0; i < blocksNumberToRender; i++) {
      const clonedAnnouncement = announcement.cloneNode(true)

      clonedAnnouncement.classList.add(this.animationClass)

      if (this.isWithFontMixed && i % 2 === 0) {
        clonedAnnouncement.classList.add(this.fontOutlineClass)
      }

      fragment.appendChild(clonedAnnouncement)
    }

    return fragment
  }

  getFragmentWithMixedFont = (
    blocksNumberToRender,
    announcement,
  ): DocumentFragment => {
    const fragment = document.createDocumentFragment()

    for (let i = 0; i < blocksNumberToRender - 1; i++) {
      const clonedAnnouncement1 = announcement.cloneNode(true)
      const clonedAnnouncement2 = announcement.cloneNode(true)

      const div = document.createElement('div')

      clonedAnnouncement1.classList.add(this.fontOutlineClass)

      div.appendChild(clonedAnnouncement1)
      div.appendChild(clonedAnnouncement2)
      div.classList.add(this.animationClass)

      fragment.appendChild(div)
    }

    return fragment
  }

  handleResize = debounce((): void => {
    const screenWidth = document.body.clientWidth

    if (screenWidth > this.initialScreenWidth) {
      this.render()
    }

    this.setAttribute('data-is-rendered', 'true')
  }, 100)

  render = (): void => {
    const { screenWidth, announcementWidth } = this.getDimentions()
    const blocksNumberToRender = this.calcQuantityToRender(
      screenWidth,
      announcementWidth,
    )

    this.renderNodes(blocksNumberToRender)
  }
}

export default ScrollingTextLine
